import React, { useRef } from "react";
import { Star, BadgeCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIOS } from "@/content";
import Reveal from "@/components/Reveal";

function Estrellas() {
  return (
    <div className="flex gap-0.5" aria-label="5 de 5 estrellas">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          size={15}
          strokeWidth={2}
          style={{ color: "var(--lima)", fill: "var(--lima)" }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function ChatMock({ chat }) {
  return (
    <div
      className="shrink-0"
      style={{
        width: 214,
        borderRadius: 28,
        padding: 8,
        background: "#2B2430",
        boxShadow: "var(--shadow-mid)",
      }}
    >
      <div
        style={{
          borderRadius: 22,
          overflow: "hidden",
          background: "#ECE5DD",
        }}
      >
        <div
          className="flex items-center gap-2 px-3 py-2"
          style={{ background: "var(--vino)" }}
        >
          <span
            className="rounded-full"
            style={{ width: 22, height: 22, background: "rgba(255,255,255,0.35)" }}
          />
          <span
            className="text-white text-[11px] font-semibold"
            style={{ filter: "blur(2.6px)" }}
          >
            {chat.nombre}
          </span>
          <span className="ml-auto text-white/70 text-[9px]">{chat.hora}</span>
        </div>
        <div className="p-2.5 space-y-2" style={{ minHeight: 168 }}>
          {chat.burbujas.map((b, i) => {
            const mine = b.de === "yo";
            return (
              <div
                key={i}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
              >
                <span
                  className="text-[11px] leading-snug px-2.5 py-1.5"
                  style={{
                    maxWidth: "86%",
                    background: mine ? "#DCF8C6" : "#FFFFFF",
                    color: "#2B2430",
                    borderRadius: 12,
                    borderTopRightRadius: mine ? 3 : 12,
                    borderTopLeftRadius: mine ? 12 : 3,
                    boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
                  }}
                >
                  {b.t}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Testimonios() {
  const railRef = useRef(null);

  const scrollBy = (dir) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  return (
    <section
      className="py-16 md:py-24"
      style={{ background: "var(--blanco)" }}
      data-testid="section-testimonios"
    >
      <div className="container-page">
        <Reveal className="text-center">
          <h2 className="h2-display" style={{ color: "var(--vino)" }}>
            Lo que me escribieron al{" "}
            <span style={{ color: "var(--lima)" }}>sexto día</span>
          </h2>
          <p className="mt-3" style={{ color: "var(--gris)", fontSize: 16 }}>
            {TESTIMONIOS.sub}
          </p>
        </Reveal>

        <div className="relative mt-9">
          <div
            ref={railRef}
            className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-5 px-5 md:mx-0 md:px-0"
            style={{ scrollSnapType: "x mandatory" }}
            data-testid="carrusel-testimonios"
          >
            {TESTIMONIOS.items.map((t, i) => (
              <Reveal
                key={t.nombre}
                delay={(i % 3) * 80}
                className="shrink-0"
                style={{
                  width: "min(78vw, 340px)",
                  scrollSnapAlign: "start",
                }}
              >
                <div
                  className="h-full p-5 md:p-6 flex flex-col"
                  style={{
                    background: i % 2 === 0 ? "var(--rosa-soft)" : "var(--lima-soft)",
                    borderRadius: 24,
                    boxShadow: "var(--shadow-soft)",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={t.foto}
                      alt={t.nombre}
                      loading="lazy"
                      className="rounded-full object-cover"
                      style={{
                        width: 64,
                        height: 64,
                        border: "3px solid #fff",
                        boxShadow: "0 6px 18px rgba(107,31,58,0.16)",
                      }}
                    />
                    <div>
                      <p
                        className="font-display"
                        style={{ fontSize: 17, color: "var(--vino)" }}
                      >
                        {t.nombre}
                      </p>
                      <p className="text-[13px]" style={{ color: "var(--gris)" }}>
                        {t.ciudad} · {t.edad} años
                      </p>
                      <div className="mt-1">
                        <Estrellas />
                      </div>
                    </div>
                  </div>

                  <p
                    className="mt-4 text-[15px] leading-relaxed grow"
                    style={{ color: "var(--carbon)" }}
                  >
                    “{t.texto}”
                  </p>

                  <span
                    className="mt-4 inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1.5 text-[12px] font-semibold"
                    style={{ background: "var(--blanco)", color: "var(--vino)" }}
                  >
                    <BadgeCheck size={14} strokeWidth={2.5} style={{ color: "var(--lima)" }} />
                    {t.genero === "f" ? TESTIMONIOS.badge : TESTIMONIOS.badgeM}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>

          {/* controles */}
          <div className="flex justify-center gap-3 mt-5">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              aria-label="Ver testimonios anteriores"
              data-testid="testimonios-prev"
              className="flex items-center justify-center rounded-full"
              style={{
                width: 44,
                height: 44,
                background: "var(--blanco)",
                border: "1.5px solid rgba(107,31,58,0.15)",
                cursor: "pointer",
              }}
            >
              <ChevronLeft size={20} style={{ color: "var(--vino)" }} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              aria-label="Ver más testimonios"
              data-testid="testimonios-next"
              className="flex items-center justify-center rounded-full"
              style={{
                width: 44,
                height: 44,
                background: "var(--blanco)",
                border: "1.5px solid rgba(107,31,58,0.15)",
                cursor: "pointer",
              }}
            >
              <ChevronRight size={20} style={{ color: "var(--vino)" }} />
            </button>
          </div>
        </div>

        {/* franja de capturas de WhatsApp */}
        <Reveal delay={120} className="mt-12">
          <div
            className="flex gap-4 overflow-x-auto no-scrollbar justify-start md:justify-center pb-2 -mx-5 px-5 md:mx-0 md:px-0"
            data-testid="capturas-whatsapp"
          >
            {TESTIMONIOS.chats.map((c) => (
              <ChatMock key={c.nombre} chat={c} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
